import {
  BodyText,
  EyebrowText,
  Heading,
  TabGroup,
  TabPanel,
} from '@postscript/components';
import React, { useState } from 'react';
import styled from 'styled-components';
import { MediaQueries } from './MainLayout';
import { StyledTabPanelText } from './SimpleStyledComponents';
import TokenBlock from './TokenBlock';

const TypeSwatchGroup = styled.div`
  padding-bottom: var(--spacing-4);

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
    margin-bottom: var(--spacing-4);
  }
`;

const TypeSwatch = styled.div`
  &:not(:last-child) {
    margin-bottom: var(--spacing-4);
  }
`;

const StyledTypeWidget = styled.div`
  ${MediaQueries.desktopOnly} {
    min-width: 600px;
  }
`;

const TypeWidget = React.forwardRef<HTMLDivElement>(({ ...props }, ref) => {
  const [currentTypeScalePanel, setCurrentTypeScalePanel] = useState('tokens');

  return (
    <StyledTypeWidget ref={ref} {...props}>
      <TabGroup
        description="Type details"
        id="type-detail-tablist"
        style={{ marginBottom: 'var(--spacing-4)' }}
      >
        <button
          data-tabpanel="type-tokens"
          data-selected={currentTypeScalePanel === 'tokens'}
          onClick={() => {
            setCurrentTypeScalePanel('tokens');
          }}
          type="button"
        >
          Tokens and CSS Variables
        </button>
        <button
          data-tabpanel="type-scale-type"
          data-selected={currentTypeScalePanel === 'type'}
          onClick={() => {
            setCurrentTypeScalePanel('type');
          }}
          type="button"
        >
          Type Scale
        </button>
      </TabGroup>

      <TabPanel id="type-tokens" selected={currentTypeScalePanel === 'tokens'}>
        <StyledTabPanelText>
          <BodyText size="small">
            Type is handled via styled React components, each with its own props
            to handle size, weight, and line-height. This helps take the
            guesswork out of implementing text. Below are the CSS variables
            consumed by the type components, and used anywhere these font styles
            need to be mimicked.
          </BodyText>
        </StyledTabPanelText>

        <TokenBlock
          name="Extra large heading"
          description="The font definition for extra large Heading component (600 4.25rem/4.5rem var(--heading-typeface))."
          variable="heading-x-large"
        />

        <TokenBlock
          name="Large heading"
          description="The font definition for large Heading component (600 3.125rem/3.375rem var(--heading-typeface))."
          variable="heading-large"
        />

        <TokenBlock
          name="Medium heading"
          description="The font definition for medium Heading component (600 2.375rem/3rem var(--heading-typeface))."
          variable="heading-medium"
        />

        <TokenBlock
          name="Small heading"
          description="The font definition for small Heading component (600 1.75rem/2.25rem var(--heading-typeface))."
          variable="heading-small"
        />

        <TokenBlock
          name="Extra small heading"
          description="The font definition for extra small Heading component (600 1.375rem/1.875rem var(--heading-typeface))."
          variable="heading-x-small"
        />

        <TokenBlock
          name="Extra (extra) small heading"
          description="The font definition for extra (extra) small Heading component (600 1rem/1.5rem var(--body-text-typeface))."
          variable="heading-xx-small"
        />

        <TokenBlock
          name="Large body text"
          description="The font definition for large BodyText component (400 1.375rem/1.875rem var(--body-text-typeface))."
          variable="body-text-large"
        />

        <TokenBlock
          name="Medium body text"
          description="The font definition for medium (normal) BodyText component (400 1rem/1.5rem var(--body-text-typeface))."
          variable="body-text-medium"
        />

        <TokenBlock
          name="Small body text"
          description="The font definition for small BodyText component (400 0.875rem/1.5rem var(--body-text-typeface))."
          variable="body-text-small"
        />

        <TokenBlock
          name="Extra small body text"
          description="The font definition for extra small BodyText component (400 0.75rem/1.125rem var(--body-text-typeface))."
          variable="body-text-x-small"
        />

        <TokenBlock
          name="Eyebrow text"
          description="The font definition for EyebrowText component (650 0.75rem/1.125rem var(--eyebrow-text-typeface))."
          variable="eyebrow-text"
        />

        <TokenBlock
          name="Heading typeface"
          description="The font-family definition for the Heading component ('roc-grotesk', 'Helvetica', sans-serif)."
          variable="heading-typeface"
        />
        <TokenBlock
          name="Wide heading typeface"
          description="The font-family definition for wide heading text (used by Studios only) ('roc-grotesk-wide', 'Inter', 'Helvetica', sans-serif)."
          variable="wide-typeface"
        />
        <TokenBlock
          name="Body text typeface"
          description="The font-family definition for the Body Text component ('Inter', 'Helvetica', sans-serif)."
          variable="body-text-typeface"
        />
        <TokenBlock
          name="Monospace typeface"
          description="The font-family definition for Code and other monospaced text components (SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier
                  New', monospace)."
          variable="mono-typeface"
        />
      </TabPanel>

      <TabPanel
        id="type-scale-type"
        selected={currentTypeScalePanel === 'type'}
      >
        <TypeSwatchGroup>
          <TypeSwatch>
            <Heading size="large">Large Heading</Heading>
            <BodyText>2.0625rem/2.5rem • 33px/40px</BodyText>
          </TypeSwatch>

          <TypeSwatch>
            <Heading size="medium">Medium Heading</Heading>
            <BodyText>1.6875rem/2.0625rem • 27px/33px</BodyText>
          </TypeSwatch>

          <TypeSwatch>
            <Heading size="small">Small Heading</Heading>
            <BodyText>1.4375rem/1.75rem • 23px/28px</BodyText>
          </TypeSwatch>

          <TypeSwatch>
            <Heading size="x-small">Extra Small Heading</Heading>
            <BodyText>1.1875rem/1.4375rem • 19px/23px</BodyText>
          </TypeSwatch>

          <TypeSwatch>
            <Heading size="xx-small">Extra (Extra) Small Heading</Heading>
            <BodyText>1rem/1.1875rem • 16px/19px</BodyText>
          </TypeSwatch>
        </TypeSwatchGroup>

        <TypeSwatchGroup>
          <TypeSwatch>
            <BodyText size="large">Large Body Text</BodyText>
            <BodyText>1.375rem/1.875rem • 22px/30px • 16.5pt/22.5pt</BodyText>
          </TypeSwatch>

          <TypeSwatch>
            <BodyText size="medium">Medium Body Text</BodyText>
            <BodyText>1rem/1.5rem • 16px/24px • 12pt/18pt</BodyText>
          </TypeSwatch>

          <TypeSwatch>
            <BodyText size="small">Small Body Text</BodyText>
            <BodyText>.875rem/1.5rem • 14px/24px • 10.5pt/18pt</BodyText>
          </TypeSwatch>

          <TypeSwatch>
            <BodyText size="x-small">Extra Small Body Text</BodyText>
            <BodyText>.75rem/1.5rem • 12px/18px • 9pt/13.5pt</BodyText>
          </TypeSwatch>
        </TypeSwatchGroup>

        <TypeSwatchGroup>
          <TypeSwatch>
            <EyebrowText>Eyebrow Text</EyebrowText>
            <BodyText>0.75rem/1.125rem • 12px/18px</BodyText>
          </TypeSwatch>
        </TypeSwatchGroup>
      </TabPanel>
    </StyledTypeWidget>
  );
});

export default TypeWidget;
